import { useLayoutEffect } from 'react';

// The app shell (__root.tsx) injects an ADAPTIVE theme script that, on OS dark
// mode, adds a `dark` class to <html> and sets documentElement.style.colorScheme
// = 'dark' (see app-context/server.ts → getAdaptiveThemeScript). That's app-wide.
// The travel-credit-card pages are designed light-only (their own fixed hex
// colours), so OS dark mode shouldn't tint form controls / scrollbars / the
// `color-scheme` UA defaults. These two exports pin `color-scheme: light` on the
// CBCC routes ONLY, leaving the rest of the app's dark mode intact.

declare global {
	interface Window {
		// stashed by forceLightSchemeScript on a hard load so the client component
		// can restore the TRUE previous theme on unmount (not the already-forced
		// light it would otherwise read live). Consumed (cleared) once read.
		__cbccPrevScheme?: { cls: boolean; cs: string };
	}
}

// SSR + hard-load (no flash): an inline <head> script that runs AFTER the root
// theme script (a route's head scripts are emitted after the shell's), so it wins
// on first paint. It stashes what it overrode on window so the client component
// can restore it on unmount. Stringified into the route head() via `children`.
export const forceLightSchemeScript = `(function(){try{var e=document.documentElement;window.__cbccPrevScheme={cls:e.classList.contains('dark'),cs:e.style.colorScheme};e.classList.remove('dark');e.style.colorScheme='light';}catch(_){}})();`;

// Client-side nav into the route (head does NOT re-run on SPA nav): force light
// before paint, and RESTORE the app's previous theme on unmount so leaving CBCC
// doesn't strip dark mode from the rest of the app. useLayoutEffect runs pre-paint
// → no flash. `prev` lives in the effect closure (no shared module state).
export function ForceLightScheme(): null {
	useLayoutEffect(() => {
		const el = document.documentElement;
		// On a hard load the head script already forced light + stashed the true
		// previous theme — consume it. On a client-nav mount there's no stash, so
		// read the live (still-adaptive) state.
		const prev = window.__cbccPrevScheme ?? {
			cls: el.classList.contains('dark'),
			cs: el.style.colorScheme,
		};
		window.__cbccPrevScheme = undefined;

		el.classList.remove('dark');
		el.style.colorScheme = 'light';

		return () => {
			if (prev.cls) el.classList.add('dark');
			el.style.colorScheme = prev.cs;
		};
	}, []);
	return null;
}
